/* ============================================================
   NP-ANIMATIONS.JS — New Paradigm
   Loading screen · Custom cursor · Scroll animations · Page transitions
   ============================================================ */

(function () {
  'use strict';

  /* ====================================================
     LOADING SCREEN
     ==================================================== */
  const NpLoader = {
    el: null,
    style: 'slide-up',   // slide-up | fade | split

    init() {
      this.el = document.getElementById('np-loader');
      if (!this.el) return;

      this.style = this.el.dataset.exitStyle || 'slide-up';

      // Minimum display time so it doesn't flash too fast
      const MIN_MS   = 1600;
      const startedAt = Date.now();

      const hide = () => {
        const elapsed = Date.now() - startedAt;
        const delay   = Math.max(0, MIN_MS - elapsed);

        setTimeout(() => {
          this._exit();
        }, delay);
      };

      if (document.readyState === 'complete') {
        hide();
      } else {
        window.addEventListener('load', hide);
        // Safety fallback — never block longer than 4s
        setTimeout(hide, 4000);
      }
    },

    _exit() {
      if (!this.el) return;

      // Directional reveal based on the page the user landed on:
      //   product → right-to-left · cart/checkout → left-to-right
      //   home → top-to-bottom · everything else → upward (default)
      var dir = this._direction();
      this.el.classList.add('np-loader--out-' + dir);
      var el = this.el;
      setTimeout(function () { if (el) el.remove(); }, 750);

      // Unlock scroll — removing the class is the real unlock (the lock is
      // scoped to `body.is-loading`). Also clear any inline overflow as a
      // belt-and-suspenders fallback.
      document.body.classList.remove('is-loading');
      document.body.style.overflow = '';
    },

    _direction() {
      var b = document.body.classList;
      if (b.contains('template-product')) return 'left';   // reveal right → left
      if (b.contains('template-cart'))    return 'right';  // reveal left → right
      if (b.contains('template-index'))   return 'down';   // reveal top → bottom
      return 'up';                                         // default
    }
  };


  /* ====================================================
     CUSTOM CURSOR
     ==================================================== */
  const NpCursor = {
    dot:   null,
    ring:  null,
    mouseX: 0,
    mouseY: 0,
    ringX:  0,
    ringY:  0,
    rafId:  null,
    LERP:   0.14,   // ring follow speed (0 = never, 1 = instant)

    init() {
      this.dot  = document.getElementById('np-cursor-dot');
      this.ring = document.getElementById('np-cursor-ring');
      if (!this.dot || !this.ring) return;

      // Don't activate on touch devices
      if (!window.matchMedia('(hover: hover)').matches) return;

      document.body.classList.add('np-cursor-active');

      // Track mouse position
      document.addEventListener('mousemove', e => {
        this.mouseX = e.clientX;
        this.mouseY = e.clientY;
        this.dot.style.left = e.clientX + 'px';
        this.dot.style.top  = e.clientY + 'px';
      });

      // Click state
      document.addEventListener('mousedown', () => document.body.classList.add('np-cursor-click'));
      document.addEventListener('mouseup',   () => document.body.classList.remove('np-cursor-click'));

      // Hover state on interactive elements
      const hoverEls = 'a, button, [role="button"], input, select, textarea, label, [data-cursor-hover]';
      document.querySelectorAll(hoverEls).forEach(el => {
        el.addEventListener('mouseenter', () => document.body.classList.add('np-cursor-hover'));
        el.addEventListener('mouseleave', () => document.body.classList.remove('np-cursor-hover'));
      });

      // Observe DOM changes to wire new elements (e.g. cart drawer opens)
      const mo = new MutationObserver(() => {
        document.querySelectorAll(hoverEls).forEach(el => {
          if (el._npCursorBound) return;
          el._npCursorBound = true;
          el.addEventListener('mouseenter', () => document.body.classList.add('np-cursor-hover'));
          el.addEventListener('mouseleave', () => document.body.classList.remove('np-cursor-hover'));
        });
      });
      mo.observe(document.body, { childList: true, subtree: true });

      // Hide ring when mouse leaves window
      document.addEventListener('mouseleave', () => {
        this.dot.style.opacity  = '0';
        this.ring.style.opacity = '0';
      });
      document.addEventListener('mouseenter', () => {
        this.dot.style.opacity  = '1';
        this.ring.style.opacity = '1';
      });

      // Start ring animation loop
      this._animateRing();
    },

    _animateRing() {
      this.ringX += (this.mouseX - this.ringX) * this.LERP;
      this.ringY += (this.mouseY - this.ringY) * this.LERP;
      this.ring.style.left = this.ringX + 'px';
      this.ring.style.top  = this.ringY + 'px';
      this.rafId = requestAnimationFrame(() => this._animateRing());
    }
  };


  /* ====================================================
     SCROLL ANIMATIONS
     ==================================================== */
  const NpScroll = {
    observer: null,

    init() {
      const elements = document.querySelectorAll('[data-np-animate]');
      if (!elements.length) return;

      // Respect reduced-motion preference
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        elements.forEach(el => el.classList.add('np-visible'));
        return;
      }

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('np-visible');
            // Stop observing unless marked as repeat
            if (!entry.target.dataset.npRepeat) {
              this.observer.unobserve(entry.target);
            }
          } else if (entry.target.dataset.npRepeat) {
            entry.target.classList.remove('np-visible');
          }
        });
      }, {
        threshold: 0.12,
        rootMargin: '0px 0px -40px 0px'
      });

      elements.forEach(el => this.observer.observe(el));

      // Auto-stagger children of [data-np-stagger] containers
      document.querySelectorAll('[data-np-stagger]').forEach(container => {
        const children = container.querySelectorAll(':scope > *:not([data-np-animate])');
        children.forEach((child, i) => {
          child.setAttribute('data-np-animate', container.dataset.npStagger || 'fade-up');
          child.setAttribute('data-np-delay', Math.min(i + 1, 8));
          this.observer.observe(child);
        });
      });
    }
  };


  /* ====================================================
     PAGE TRANSITIONS
     ==================================================== */
  const NpPageTransition = {
    overlay: null,
    enabled: false,

    init() {
      this.overlay = document.getElementById('np-page-overlay');
      if (!this.overlay) return;
      this.enabled = true;

      // Intercept internal link clicks
      document.addEventListener('click', e => {
        const link = e.target.closest('a[href]');
        if (!link) return;

        const href = link.getAttribute('href');
        // Skip anchors, external, download, or special links
        if (
          !href ||
          href.startsWith('#') ||
          href.startsWith('mailto:') ||
          href.startsWith('tel:') ||
          link.target === '_blank' ||
          link.hasAttribute('download') ||
          e.ctrlKey || e.metaKey || e.shiftKey
        ) return;

        // Same origin only
        try {
          const url = new URL(href, window.location.href);
          if (url.origin !== window.location.origin) return;
        } catch (_) { return; }

        e.preventDefault();
        this._leave(href);
      });

      // Trigger enter animation on load
      this._enter();
    },

    // Reveal on load — direction is whatever was stored when leaving.
    _enter() {
      var dir = 'up';
      try { dir = sessionStorage.getItem('npDir') || 'up'; sessionStorage.removeItem('npDir'); } catch (e) {}
      var ov = this.overlay;
      ov.classList.add('np-reveal-' + dir);
      setTimeout(function () { ov.classList.remove('np-reveal-' + dir); }, 600);
    },

    // Cover on click — direction depends on where the user is heading.
    _leave(href) {
      var dir = this._dir(href);
      try { sessionStorage.setItem('npDir', dir); } catch (e) {}
      this.overlay.classList.add('np-cover-' + dir);
      setTimeout(function () { window.location.href = href; }, 450);
    },

    _dir(href) {
      try {
        var p = new URL(href, window.location.href).pathname.replace(/\/+$/, '') || '/';
        if (p === '/') return 'down';                              // home → top-to-bottom
        if (p.indexOf('/products/') !== -1) return 'left';         // product → right-to-left
        if (p.indexOf('/cart') !== -1 || p.indexOf('/checkout') !== -1) return 'right'; // checkout → left-to-right
      } catch (e) {}
      return 'up';
    }
  };


  /* ====================================================
     BOOT — initialise everything on DOMContentLoaded
     ==================================================== */
  function boot() {
    NpLoader.init();
    NpCursor.init();
    NpScroll.init();
    NpPageTransition.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Expose public API for Shopify theme / other scripts
  window.NewParadigm = window.NewParadigm || {};
  Object.assign(window.NewParadigm, {
    loader:    NpLoader,
    cursor:    NpCursor,
    scroll:    NpScroll,
    pageTransition: NpPageTransition
  });

})();
